/* ===========================================================
   Cart upsell — 1-click AJAX add for recommendations & bundle.
   Reuses the theme's existing cart plumbing:
     - POST /cart/add.js with `sections` (Section Rendering API)
     - `cart:section` event  -> header drawer re-renders in place
     - `cart:open` event      -> opens the drawer (only when not on cart page)
   On the cart page it also re-renders the cart section (`main`) and this
   upsell section (`cart-upsell`) so added items appear and drop out of the row.
   =========================================================== */
(() => {
  if (window.__cartUpsellBound) return;
  window.__cartUpsellBound = true;

  const cartRoot =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  const reduceMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* Brand-coloured confetti pop at the button. Appended to <body> so it
     survives the cart/drawer section swap that follows an add. */
  function burstAt(el) {
    if (reduceMotion || !el || !el.getBoundingClientRect) return;
    const r = el.getBoundingClientRect();
    if (!r.width) return;
    const css = getComputedStyle(document.documentElement);
    const colors = [
      (css.getPropertyValue("--color-highlight") || "#E55B2C").trim(),
      (css.getPropertyValue("--color-fg") || "#1A1A1A").trim(),
      (css.getPropertyValue("--color-muted") || "#E7E3DC").trim(),
    ];
    const layer = document.createElement("div");
    layer.className = "cart-upsell-burst";
    layer.style.left = r.left + r.width / 2 + "px";
    layer.style.top = r.top + r.height / 2 + "px";
    const n = 11;
    for (let i = 0; i < n; i++) {
      const p = document.createElement("span");
      const ang = (Math.PI * 2 * i) / n + (Math.random() - 0.5) * 0.6;
      const dist = 30 + Math.random() * 26;
      p.style.setProperty("--bx", (Math.cos(ang) * dist).toFixed(1) + "px");
      p.style.setProperty("--by", (Math.sin(ang) * dist).toFixed(1) + "px");
      p.style.background = colors[i % colors.length];
      p.style.animationDelay = Math.round(Math.random() * 60) + "ms";
      layer.appendChild(p);
    }
    document.body.appendChild(layer);
    window.setTimeout(() => layer.remove(), 900);
  }

  /* Pop the header cart-count badge after it re-renders. */
  function bounceCount() {
    const c = document.querySelector("[data-cart-count]");
    if (!c || reduceMotion) return;
    c.classList.remove("cart-count-bounce");
    void c.offsetWidth;
    c.classList.add("cart-count-bounce");
    window.setTimeout(() => c.classList.remove("cart-count-bounce"), 700);
  }

  /* The cart page renders the `main` (cart) + `cart-upsell` sections. */
  function onCartPage() {
    return !!document.querySelector("#shopify-section-main .cart-template");
  }

  /* Re-revealed swapped content (theme.js reveal observer only runs once). */
  function reveal(scope) {
    if (!scope) return;
    if (scope.hasAttribute && scope.hasAttribute("data-reveal")) {
      scope.classList.add("is-visible");
    }
    scope
      .querySelectorAll?.("[data-reveal]")
      .forEach((el) => el.classList.add("is-visible"));
  }

  function swapSection(id, markup) {
    if (!markup) return;
    const current = document.getElementById("shopify-section-" + id);
    if (!current) return;
    const fresh = new DOMParser()
      .parseFromString(markup, "text/html")
      .getElementById("shopify-section-" + id);
    if (!fresh) return;
    current.replaceWith(fresh);
    reveal(fresh);
  }

  function setState(btn, state, root) {
    if (!btn) return;
    const labelEl = btn.querySelector(".cart-upsell__add-label");
    const original = btn.getAttribute("data-original-label");
    if (original == null && labelEl) {
      btn.setAttribute("data-original-label", labelEl.textContent);
    }
    const data = (key, fallback) =>
      (root && root.getAttribute("data-" + key)) || fallback;

    btn.classList.remove("is-loading", "is-added");
    if (state === "loading") {
      btn.classList.add("is-loading");
      btn.disabled = true;
      if (labelEl) labelEl.textContent = data("adding-label", "Adding…");
    } else if (state === "added") {
      btn.classList.add("is-added");
      btn.disabled = true;
      if (labelEl) labelEl.textContent = data("added-label", "Added");
    } else if (state === "error") {
      btn.disabled = false;
      if (labelEl) labelEl.textContent = data("error-label", "Try again");
    } else {
      btn.disabled = false;
      if (labelEl) labelEl.textContent = btn.getAttribute("data-original-label");
    }
  }

  function announce(root, message) {
    const live = root && root.querySelector("[data-upsell-live]");
    if (live) live.textContent = message;
  }

  async function addItems(items, btn, root) {
    if (!items.length) return;
    const cartPage = onCartPage();
    const sections = cartPage ? ["header", "main", "cart-upsell"] : ["header"];

    setState(btn, "loading", root);
    try {
      const res = await fetch(cartRoot + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items,
          sections: sections.join(","),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.description || data.message || "Could not add to cart");
      }

      /* Celebrate before any DOM swap removes the button. */
      burstAt(btn);

      /* Keep the header count + drawer in sync everywhere. */
      if (data.sections && data.sections.header) {
        document.dispatchEvent(
          new CustomEvent("cart:section", { detail: { html: data.sections.header } })
        );
        bounceCount();
      }

      if (cartPage && data.sections) {
        swapSection("main", data.sections.main);
        swapSection("cart-upsell", data.sections["cart-upsell"]);
      } else {
        /* Added from the drawer (or elsewhere) — surface the drawer to confirm. */
        document.dispatchEvent(new CustomEvent("cart:open"));
        setState(btn, "added", root);
        announce(root, addedMessage(items, root));
        window.setTimeout(() => setState(btn, "reset", root), 1600);
      }
    } catch (err) {
      setState(btn, "error", root);
      announce(root, (err && err.message) || "Could not add to cart");
      window.setTimeout(() => setState(btn, "reset", root), 2200);
    }
  }

  function addedMessage(items, root) {
    const count = items.reduce((n, i) => n + (i.quantity || 1), 0);
    return count > 1 ? count + " items added to cart" : "Added to cart";
  }

  document.addEventListener("click", (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    /* Single product add (recommendation card). */
    const addBtn = target.closest("[data-upsell-add]");
    if (addBtn) {
      e.preventDefault();
      const card = addBtn.closest("[data-upsell-card]");
      const root = addBtn.closest("[data-cart-upsell]");
      const select = card && card.querySelector("[data-upsell-variant]");
      const variantId = select ? select.value : addBtn.getAttribute("data-variant-id");
      if (variantId) addItems([{ id: Number(variantId), quantity: 1 }], addBtn, root);
      return;
    }

    /* Bundle "add all". */
    const addAllBtn = target.closest("[data-upsell-add-all]");
    if (addAllBtn) {
      e.preventDefault();
      const root = addAllBtn.closest("[data-cart-upsell]");
      const ids = (addAllBtn.getAttribute("data-variant-ids") || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const items = ids.map((id) => ({ id: Number(id), quantity: 1 }));
      if (items.length) addItems(items, addAllBtn, root);
    }
  });
})();
